import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@base/Button/Button';

import {
  fetchCombatDetails,
  enrollForContest,
  setModalState
} from '@actions/combats';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getIsLoggedIn } from '@selectors/auth';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getIsFirstContest } from '@api/selectors/auth';
import { getCombatDetails, getIsEnrolled } from '@selectors/combats';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import { Spacings } from '@styles/index';
import CalenderCheck from '@icon/CalenderCheck';
import {
  COMBAT_ENROLLED,
  COMBAT_DEAILS_VIEWED,
  COMBAT_STARTED
} from '@constants/segment';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import { useSnackbar } from '@stores/snackbar';
import useCombatSegmentAnalytics from '../useCombatSegmentAnalytics';
import { MODAL_TYPES } from '../../constants';

const ActionsContainer = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  display: grid;
  grid-template: 1fr / repeat(2, max-content);
  grid-column-gap: 10px;
`;

const Actions = ({ isPast, isLive, data }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { open: showSnackbar } = useSnackbar();
  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const {
    query: { goalSlug, goalUID }
  } = router;

  const { name, code, uid, slug, goal } = data;
  const starts_at = data?.starts_at || data?.startsAt;
  const is_enrolled = data?.is_enrolled || data?.isEnrolled;
  const enroll_credits = data?.enroll_credits || data?.enrollCredits;
  const quiz_details = data?.quiz_details || data?.quizDetails;
  const availableLanguages =
    quiz_details?.available_languages || quiz_details?.availableLanguages;

  const combat = useSelectorWithShallowEqual(getCombatDetails(uid));
  const isEnrolled = useSelectorWithShallowEqual(getIsEnrolled(uid));
  const { isMobile } = useWindowSize();

  const isLoggedIn = useSelectorWithShallowEqual(getIsLoggedIn);
  const { accessControl } = useAccessControl();
  const isFirstContest = getIsFirstContest(accessControl);
  const isPlusEnrolled = useSelectorWithShallowEqual(
    getIsSubscribedToPlus(goalUID)
  );
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));

  const segmentAnalytics = useCombatSegmentAnalytics();
  const isEnrolledInCombat = isEnrolled || is_enrolled;

  const showError = (message) => {
    showSnackbar({
      message,
      type: 'error'
    });
  };

  const sendEnrollEvent = () => {
    segmentAnalytics(COMBAT_ENROLLED, {
      combat_invite_code: 'NA',
      credits_applied: enroll_credits,
      combat_class: 'NA',
      combat_uid: uid,
      combat_title: name,
      combat_start_date: starts_at,
      combat_duration: quiz_details?.duration || 0,
      combat_user_level: 'NA',
      combat_user_state: isFirstContest ? 'New' : 'Repeat',
      goal_name: goal?.name ?? 'NA',
      is_enrolled: isEnrolled,
      last_primary_source_section: 'NA'
    });
  };

  const sendViewDetailsEvent = () => {
    segmentAnalytics(COMBAT_DEAILS_VIEWED, {
      combat_class: 'NA',
      combat_uid: uid,
      combat_title: name,
      combat_start_date: starts_at,
      combat_duration: quiz_details?.duration || 0,
      combat_user_level: 'NA',
      combat_user_state: isFirstContest ? 'New' : 'Repeat',
      goal_name: goal?.name ?? 'NA',
      is_enrolled: isEnrolled,
      last_primary_source_section: 'NA'
    });
  };

  const handleOnEnroll = () => {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (!(isPlusEnrolled || isOfflineSubscription) && isFirstContest) {
      dispatch(
        setModalState({
          type: MODAL_TYPES.SHOW_INVITE_CODE_DRAWER,
          data: {
            uid,
            starts_at,
            title: name,
            duration: quiz_details?.duration || 0,
            credits: enroll_credits,
            class: 'NA'
          }
        })
      );
    } else {
      dispatch(
        enrollForContest({
          contestUID: uid,
          inviteCode: ''
        })
      ).then((res) => {
        if (res.error) {
          return showError('Something went wrong');
        }
        sendEnrollEvent();
      });
    }
  };

  const handleOnJoin = () => {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (isMobile) {
      showError('Combat can be taken only on the app');
      return;
    }

    if (!isEnrolledInCombat) {
      showError('You need to enroll before joining the combat!');
      return;
    }

    const hasMultipleLanguages = availableLanguages.length > 1;
    const pathname =
      '/goal/[goalSlug]/[goalUID]/combat/[combatSlug]/[combatUID]';
    const query = {
      goalSlug,
      goalUID,
      combatSlug: slug,
      combatUID: quiz_details.uid,
      code,
      language: availableLanguages[0].code
    };

    if (hasMultipleLanguages) {
      dispatch(
        setModalState({
          type: MODAL_TYPES.COMBAT_LANGUAGE_SELECTION,
          data: {
            pathname,
            query,
            languages: availableLanguages,
            combatData: data
          }
        })
      );

      return;
    }

    segmentAnalytics(COMBAT_STARTED, {
      combat_class: 'NA',
      combat_uid: uid,
      combat_title: name,
      combat_start_date: starts_at,
      combat_duration: quiz_details?.duration || 0,
      combat_user_level: 'NA',
      combat_user_state: isFirstContest ? 'New' : 'Repeat',
      credits_applied: enroll_credits,
      goal_name: goal.name,
      is_enrolled,
      is_resumed: 'NA',
      join_time_delay: 'NA',
      last_primary_source_section: 'NA'
    });

    pushRoute({
      pathname,
      query
    });
  };

  const handleOnViewResults = () => {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }

    if (isMobile) {
      showError('Result can be viewed only on the app');
      return;
    }

    pushRoute({
      pathname:
        '/goal/[goalSlug]/[goalUID]/combat/[combatSlug]/[combatUID]/result',
      query: {
        goalSlug,
        goalUID,
        combatSlug: slug,
        combatUID: quiz_details.uid
      }
    });
  };

  const handleOnViewDetails = () => {
    if (isEmpty(combat)) {
      dispatch(fetchCombatDetails(uid));
    }

    dispatch(
      setModalState({
        type: MODAL_TYPES.SHOW_COMBAT_DETAILS,
        data: {
          uid,
          name,
          goalName: goal.name,
          startsAt: starts_at
        }
      })
    );

    sendViewDetailsEvent();
  };

  const getContent = () => {
    if (isPast) {
      return (
        <Button
          grid={3}
          label="View results"
          variant="hollow"
          color="black"
          onClick={handleOnViewResults}
        />
      );
    }

    return (
      <>
        {isLive && isEnrolledInCombat ? (
          <Button
            grid={3}
            label="Join now"
            variant="filled"
            color="green"
            onClick={handleOnJoin}
          />
        ) : (
          <Button
            grid={3}
            variant={isEnrolledInCombat ? 'disabled' : 'filled'}
            label={isEnrolledInCombat ? 'Enrolled' : 'Enroll'}
            color={isEnrolledInCombat ? 'black' : 'blue'}
            icon={
              isEnrolledInCombat ? (
                <CalenderCheck
                  size={Spacings.SPACING_6B}
                  viewBox="0 0 24 24"
                  color="var(--color-text-secondary)"
                />
              ) : null
            }
            onClick={handleOnEnroll}
          />
        )}
        <Button
          grid={3}
          label="View details"
          variant="hollow"
          color="black"
          onClick={handleOnViewDetails}
        />
      </>
    );
  };

  return <ActionsContainer>{getContent()}</ActionsContainer>;
};

Actions.propTypes = {
  isLive: PropTypes.bool.isRequired,
  isPast: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired
};

export default Actions;
